// Smart Quote Form configuration — categories, sizes, materials, add-ons
export const QUOTE_CATEGORIES = [
  { id: "gazebo", label: "Gazebo", emoji: "🏛️", popular: true, hasMaterial: true, hasRoof: true },
  { id: "pergola", label: "Pergola", emoji: "🌿", popular: true, hasMaterial: true, hasRoof: true },
  { id: "shed", label: "Sheds", emoji: "🏚️", hasShed: true },
  { id: "sauna", label: "Sauna", emoji: "♨️" },
  { id: "gym", label: "Gym Equipment", emoji: "💪" },
  { id: "playground", label: "Playground", emoji: "🎠" },
  { id: "outdoor-furniture", label: "Outdoor Furniture", emoji: "🛋️" },
  { id: "outdoor-structures", label: "Outdoor Structures", emoji: "🏗️", hasMaterial: true, hasRoof: true },
];

export const GAZEBO_SIZES = [
  "8×8",
  "8×10",
  "10×10",
  "10×12",
  "10×14",
  "12×12",
  "12×14",
  "12×16",
  "12×20",
  "12×24",
  "14×20",
  "Custom Size",
];

export const SIZE_OPTIONS = GAZEBO_SIZES; // backward compat alias

export const STRUCTURE_MATERIALS = [
  "Aluminum",
  "Steel",
  "Cedar Wood",
  "Metal Frame",
  "Pressure Treated Wood",
  "Composite Material",
];

export const ROOF_TYPES = [
  "Hardtop",
  "Soft Top",
  "Polycarbonate Roof",
  "Galvanized Steel Roof",
  "Louvered Roof",
  "Insulated Roof",
];

export const SHED_MATERIALS = [
  "Plastic Shed",
  "Resin Shed",
  "Metal Shed",
  "Wooden Shed",
];

export const FOUNDATION_TYPES = [
  "Existing Concrete Slab",
  "Existing Pavers",
  "Existing Gravel Area",
  "Need Wood Platform",
  "Need Concrete Foundation",
  "Need Gravel Base",
];

export const PLATFORM_OPTIONS = [
  "Pressure Treated Platform",
  "Elevated Platform",
  "Reinforced Heavy-Duty Base",
];

export const INSTALL_LOCATIONS = [
  "Backyard",
  "Front yard",
  "Side yard",
  "Deck / Patio",
  "Rooftop",
  "Other",
];

export const GROUND_TYPES = [
  "Grass / Lawn",
  "Concrete / Slab",
  "Pavers",
  "Wood deck",
  "Gravel",
  "Dirt — needs leveling",
  "Not sure",
];

export const COMPLEXITY_LEVELS = [
  { id: "simple", label: "Simple", desc: "Standard kit, level ground" },
  { id: "moderate", label: "Moderate", desc: "Anchoring or basic prep" },
  { id: "complex", label: "Complex", desc: "Multiple add-ons / unique site" },
];

export const ACCESS_LEVELS = [
  { id: "easy", label: "Easy", desc: "Truck can reach, wide gate" },
  { id: "moderate", label: "Moderate", desc: "Narrow gate / steps" },
  { id: "difficult", label: "Difficult", desc: "Crane / hand-carry only" },
];

// Premium accessories (large grid)
export const ACCESSORY_OPTIONS = [
  { id: "heater", label: "Heater Installation", emoji: "🔥", price: "+$220" },
  { id: "led-lighting", label: "LED Lighting", emoji: "💡", price: "+$180", badge: "Most popular" },
  { id: "ceiling-fan", label: "Ceiling Fan", emoji: "🌀", price: "+$240" },
  { id: "mosquito-net", label: "Mosquito Net", emoji: "🦟", price: "+$140" },
  { id: "privacy-curtains", label: "Privacy Curtains", emoji: "🪟", price: "+$160" },
  { id: "roller-blinds", label: "Roller Blinds", emoji: "🪟", price: "+$280" },
  { id: "privacy-wall", label: "Privacy Wall", emoji: "🧱", price: "+$320" },
  { id: "country-bar", label: "Country Bar", emoji: "🍸", price: "+$450", badge: "Premium" },
  { id: "tv-mount", label: "TV Mount", emoji: "📺", price: "+$150" },
  { id: "electrical-kit", label: "Electrical Kit", emoji: "⚡", price: "+$260" },
  { id: "decorative-panels", label: "Decorative Panels", emoji: "🎨", price: "+$190" },
  { id: "gutter-system", label: "Gutter System", emoji: "🌧️", price: "+$210" },
];

// Anchoring & cleanup with default prices (admin can override later)
export const ANCHORING_OPTIONS = [
  { id: "anchor-concrete", label: "Anchoring to Concrete", price: "+$140" },
  { id: "anchor-pavers", label: "Anchoring to Pavers", price: "+$160" },
  { id: "anchor-deck", label: "Anchoring to Deck", price: "+$120" },
];

export const CLEANUP_OPTIONS = [
  { id: "trash-removal", label: "Trash Removal", price: "+$95" },
  { id: "cardboard-removal", label: "Cardboard Removal", price: "+$45" },
  { id: "plastic-removal", label: "Plastic Removal", price: "+$35" },
  { id: "pallet-disposal", label: "Pallet Disposal", price: "+$65" },
  { id: "full-cleanup", label: "Full Cleanup Service", price: "+$220", badge: "Recommended" },
];

// Legacy alias used by older code
export const ADDON_OPTIONS = [
  ...ANCHORING_OPTIONS.map((a) => ({ ...a, desc: "Secure ground anchor" })),
  ...CLEANUP_OPTIONS.map((c) => ({ ...c, desc: "Site cleanup service" })),
];

export const BOXES_OPTIONS = [
  "Not delivered yet",
  "1 box",
  "2 boxes",
  "3 boxes",
  "4+ boxes",
  "Pallet",
];

// Retailers where products are typically purchased
export const PURCHASED_FROM_OPTIONS = [
  "Costco",
  "Amazon",
  "Yardistry",
  "Home Depot",
  "Lowe's",
  "Wayfair",
  "Sam's Club",
  "Walmart",
  "Sunjoy",
  "Backyard Discovery",
  "Direct from manufacturer",
  "Other",
];

// Delivery progress for the product itself
export const DELIVERY_STATUS_OPTIONS = [
  { id: "delivered", label: "Delivered", desc: "It's already at my property" },
  { id: "scheduled", label: "Scheduled delivery", desc: "I know when it arrives" },
  { id: "not-yet", label: "Not delivered yet", desc: "Still ordering / shipping" },
];

// Existing site surface for platform/foundation question
export const EXISTING_SURFACE_OPTIONS = [
  { id: "concrete", label: "Existing concrete slab" },
  { id: "pavers", label: "Existing pavers" },
  { id: "gravel", label: "Existing gravel pad" },
  { id: "wood-deck", label: "Existing wood deck" },
  { id: "none", label: "None — bare ground" },
];

// Required surface type at the install spot (replaces free-form ground type)
export const SURFACE_TYPE_OPTIONS = [
  { id: "concrete", label: "Concrete" },
  { id: "pavers", label: "Pavers" },
  { id: "grass", label: "Grass" },
  { id: "dirt", label: "Dirt" },
  { id: "deck", label: "Deck" },
  { id: "other", label: "Other" },
];

// Preferred install time window
export const TIME_WINDOW_OPTIONS = [
  { id: "morning", label: "Morning", desc: "8 AM – 12 PM" },
  { id: "afternoon", label: "Afternoon", desc: "12 PM – 4 PM" },
  { id: "evening", label: "Evening", desc: "4 PM – 7 PM" },
  { id: "flexible", label: "Flexible", desc: "Any time of day" },
];

// Required Yes/No add-on prompts (each gates progression)
export const ANCHORING_COUNT_OPTIONS = [
  { id: "4", label: "4 anchors", price: "$120" },
  { id: "6", label: "6 anchors", price: "$180" },
];

// Optional installation add-ons (not required Yes/No — toggle list)
export const OPTIONAL_ADDONS = [
  { id: "cardboard-removal", label: "Box Breakdown Only (Left On-site)", price: "+$45" },
  { id: "disposal-service", label: "Box Breakdown + Removal", price: "+$120" },
];

// Per-unit pricing for quantity-driven add-ons (used in emails & admin)
export const UNIT_PRICES = {
  pallet_removal: 45,   // $45 per pallet
};

// Site-condition acknowledgments — all required before submit
export const ACKNOWLEDGMENTS = [
  {
    id: "ack_15ft",
    label: "Product boxes within 15 ft of install area",
    text:
      "I understand the product boxes must be located within 15 feet of the final installation area. If boxes, pallets, or materials are farther than 15 ft, an additional material handling fee of $80 may apply.",
  },
  {
    id: "ack_clear_access",
    label: "Path and install area will be clear",
    text:
      "I confirm the installation path and area will be clear and accessible before the crew arrives — free of furniture, storage, vehicles, construction or landscaping materials, debris, or any other obstacles.",
  },
  {
    id: "ack_site_prep",
    label: "Site prep fee may apply ($80)",
    text:
      "I understand that if the crew is required to move furniture, materials, boxes, equipment, landscaping items, or other obstacles before installation can begin, an additional site preparation fee of $80 may apply.",
  },
  {
    id: "ack_access_conditions",
    label: "Access conditions",
    text:
      "I understand installation pricing assumes normal access. Additional charges may apply for stairs, steep hills, long carrying distances, restricted access, difficult terrain, or unusual site conditions.",
  },
  {
    id: "ack_price_protection",
    label: "Final price may be adjusted",
    text:
      "I understand that final pricing may be adjusted if actual job-site conditions, product specifications, quantities, accessories, or installation requirements differ from the information provided during booking.",
  },
];
